/**
 * Apercu du rendu de l'eleve, isole dans une iframe.
 *
 * L'implementation precedente injectait `<style>` + HTML directement dans la
 * page via `dangerouslySetInnerHTML`. Le CSS de l'eleve s'appliquait donc a
 * toute la plateforme (`body { display: none }` suffisait a la faire
 * disparaitre), et le rendu heritait des styles d'Ant Design, ce qui le rendait
 * infidele a ce que produirait un vrai navigateur.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { HtmlCssWorkspace } from '../core/types';
import { PREVIEW_SANDBOX, buildPreviewDocument } from '../core/preview';

interface HtmlCssPreviewProps {
  workspace: HtmlCssWorkspace;
  /**
   * Delai avant repercussion des frappes, en millisecondes.
   * Evite de reconstruire le document a chaque caractere saisi.
   */
  debounceMs?: number;
  title?: string;
}

const HtmlCssPreview: React.FC<HtmlCssPreviewProps> = ({
  workspace,
  debounceMs = 300,
  title = 'Apercu du rendu',
}) => {
  // Le document affiche suit le code de l'eleve avec un leger differe.
  const [rendered, setRendered] = useState<HtmlCssWorkspace>(workspace);
  const isFirstRender = useRef(true);

  useEffect(() => {
    // Le premier rendu est immediat : attendre le debounce ferait clignoter un
    // apercu vide a l'ouverture de l'exercice.
    if (isFirstRender.current) {
      isFirstRender.current = false;
      setRendered(workspace);
      return;
    }

    const timer = window.setTimeout(() => setRendered(workspace), debounceMs);
    return () => window.clearTimeout(timer);
  }, [workspace, debounceMs]);

  const document = useMemo(() => buildPreviewDocument(rendered), [rendered]);

  return (
    <iframe
      // `srcDoc` remplace le contenu sans recreer l'element : l'iframe n'est
      // pas remontee a chaque frappe, contrairement a l'ancienne `key`
      // incrementee qui demontait tout le sous-arbre.
      srcDoc={document}
      title={title}
      sandbox={PREVIEW_SANDBOX}
      className="htmlcss-preview-frame"
    />
  );
};

export default HtmlCssPreview;
